import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { buildApiUrl } from "../config/api";
import "./Header.css";
import "./HeaderMobile.css";

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { user, isLoggedIn, logout } = useAuth();
  const { cartCount } = useCart();

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const loadWishlistCount = useCallback(async () => {
    if (!isLoggedIn) {
      setWishlistCount(0);
      return;
    }

    try {
      const response = await fetch(buildApiUrl("/wishlist"), {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setWishlistCount(Number(data.count || data.items?.length || 0));
      }
    } catch (error) {
      console.error("Header wishlist count error:", error);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadWishlistCount();
  }, [loadWishlistCount]);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateHeader = () => {
      const currentScrollY = Math.max(window.scrollY, 0);
      const delta = currentScrollY - lastScrollY;

      setIsHeaderScrolled(currentScrollY > 24);

      if (isMobileMenuOpen || currentScrollY < 80) {
        setIsHeaderVisible(true);
      } else if (delta > 5) {
        setIsHeaderVisible(false);
      } else if (delta < -3) {
        setIsHeaderVisible(true);
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    updateHeader();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleWishlistUpdated = () => {
      loadWishlistCount();
    };

    window.addEventListener("mayachitro:wishlist-updated", handleWishlistUpdated);

    return () => {
      window.removeEventListener(
        "mayachitro:wishlist-updated",
        handleWishlistUpdated
      );
    };
  }, [loadWishlistCount]);

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }

    closeMobileMenu();
  };

  const handleLogoKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleLogoClick();
    }
  };

  const handleLogout = async () => {
    await logout();
    setWishlistCount(0);
    closeMobileMenu();
    navigate("/login", { replace: true });
  };

  const accountLink = isLoggedIn ? "/account" : "/login?redirect=/account";
  const accountTitle = isLoggedIn
    ? `Account: ${user?.fullName || "MayaChitro Customer"}`
    : "Login / Create Account";

  const wishlistTitle =
    isLoggedIn && wishlistCount > 0
      ? `Wishlist: ${wishlistCount} saved item${wishlistCount > 1 ? "s" : ""}`
      : "Wishlist";

  return (
    <header
      className={`site-header ${location.pathname === "/" ? "header-home" : "header-inner-page"} ${
        isHeaderScrolled ? "header-scrolled" : "header-at-top"
      } ${isHeaderVisible ? "header-visible" : "header-hidden"} ${
        isMobileMenuOpen ? "header-menu-open" : ""
      }`}
    >
      <div
        className="header-brand"
        onClick={handleLogoClick}
        onKeyDown={handleLogoKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Go to MayaChitro home"
      >
        <span className="brand-script">MayaChitro</span>
        <span className="brand-tagline">Visual Stories. Timeless Moments.</span>
      </div>

      <nav
        className={`header-nav ${isMobileMenuOpen ? "open" : ""}`}
        aria-label="Main navigation"
      >
        <button
          className="mobile-close"
          onClick={closeMobileMenu}
          type="button"
          aria-label="Close menu"
        >
          ×
        </button>

        <NavLink to="/shop" onClick={closeMobileMenu}>
          Shop
        </NavLink>

        <NavLink to="/collections" onClick={closeMobileMenu}>
          Collections
        </NavLink>

        <NavLink to="/stories" onClick={closeMobileMenu}>
          Photo Stories
        </NavLink>

        <NavLink to="/licensing" onClick={closeMobileMenu}>
          Licensing
        </NavLink>

        <NavLink to="/about" onClick={closeMobileMenu}>
          About
        </NavLink>

        <div className="mobile-nav-actions">
          <Link to="/shop" onClick={closeMobileMenu}>
            <span>⌕</span>
            Search
          </Link>

          <Link to="/wishlist" onClick={closeMobileMenu}>
            <span>♡</span>
            Wishlist
            {isLoggedIn && wishlistCount > 0 && (
              <em className="mobile-action-count">{wishlistCount}</em>
            )}
          </Link>

          <Link to="/cart" onClick={closeMobileMenu}>
            <span>🛒</span>
            Cart
            {cartCount > 0 && (
              <em className="mobile-action-count">{cartCount}</em>
            )}
          </Link>

          <Link to={accountLink} onClick={closeMobileMenu}>
            <span>◎</span>
            {isLoggedIn ? "Account" : "Login"}
          </Link>

          {isLoggedIn && (
            <>
              <Link to="/my-orders" onClick={closeMobileMenu}>
                <span>▤</span>
                Orders
              </Link>

              <Link to="/my-downloads" onClick={closeMobileMenu}>
                <span>⇩</span>
                Downloads
              </Link>

              <button type="button" onClick={handleLogout}>
                <span>↳</span>
                Logout
              </button>
            </>
          )}
        </div>
      </nav>

      <div className="header-actions" aria-label="Shop actions">
        <Link to="/shop" aria-label="Search products" title="Search">
          ⌕
        </Link>

        <Link
          to="/wishlist"
          aria-label={wishlistTitle}
          title={wishlistTitle}
          className={`wishlist-link ${wishlistCount > 0 ? "has-count" : ""}`}
        >
          ♡
          {isLoggedIn && wishlistCount > 0 && (
            <span className="wishlist-count-badge">{wishlistCount}</span>
          )}
        </Link>

        <Link to="/cart" aria-label="Cart" title="Cart" className="cart-link">
          🛒
          {cartCount > 0 && <span>{cartCount}</span>}
        </Link>

        <div className="header-account-wrap">
          <Link
            to={accountLink}
            className={`header-account-link ${isLoggedIn ? "logged-in" : ""}`}
            aria-label={accountTitle}
            title={accountTitle}
          >
            ◎
          </Link>

          <div className="header-account-menu">
            {isLoggedIn ? (
              <>
                <div className="account-menu-user">
                  <strong>{user?.fullName || "MayaChitro Customer"}</strong>
                  <small>{user?.email}</small>
                </div>

                <Link to="/account">My Account</Link>

                <Link to="/wishlist" className="account-menu-row">
                  Wishlist
                  {wishlistCount > 0 && (
                    <span className="account-menu-count">{wishlistCount}</span>
                  )}
                </Link>

                <Link to="/my-orders">My Orders</Link>
                <Link to="/my-downloads">My Downloads</Link>

                {user?.role === "admin" && <Link to="/admin">Admin Dashboard</Link>}

                <button type="button" onClick={handleLogout}>
                  Logout
                </button>
              </>
            ) : (
              <>
                <div className="account-menu-user">
                  <strong>Welcome to MayaChitro</strong>
                  <small>Login to save wishlist and access downloads.</small>
                </div>

                <Link to="/login">Login</Link>
                <Link to="/register">Create Account</Link>
              </>
            )}
          </div>
        </div>
      </div>

      <button
        className={`hamburger ${isMobileMenuOpen ? "open" : ""}`}
        onClick={() => setIsMobileMenuOpen((value) => !value)}
        type="button"
        aria-label="Open menu"
        aria-expanded={isMobileMenuOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <aside
        className={`desktop-menu-drawer ${isMobileMenuOpen ? "open" : ""}`}
        aria-hidden={!isMobileMenuOpen}
        aria-label="MayaChitro menu drawer"
      >
        <div className="drawer-topline">
          <div>
            <span className="drawer-kicker">Explore MayaChitro</span>
            <strong>Visual stories,<br />beautifully collected.</strong>
          </div>
          <button type="button" onClick={closeMobileMenu} aria-label="Close menu">×</button>
        </div>

        <nav className="drawer-links" aria-label="Drawer navigation">
          <Link to="/shop?brand=mayachitro" onClick={closeMobileMenu}>Shop Photography <span>01</span></Link>
          <Link to="/collections" onClick={closeMobileMenu}>Collections <span>02</span></Link>
          <Link to="/stories" onClick={closeMobileMenu}>Photo Stories <span>03</span></Link>
          <Link to="/licensing" onClick={closeMobileMenu}>Image Licensing <span>04</span></Link>
          <Link to="/about" onClick={closeMobileMenu}>About MayaChitro <span>05</span></Link>
          <Link to="/contact" onClick={closeMobileMenu}>Contact <span>06</span></Link>
        </nav>

        <div className="drawer-footer-actions">
          <Link to={accountLink} onClick={closeMobileMenu}>{isLoggedIn ? "My account" : "Sign in"}</Link>
          <Link to="/wishlist" onClick={closeMobileMenu}>Wishlist{wishlistCount > 0 ? ` (${wishlistCount})` : ""}</Link>
        </div>
      </aside>

      {isMobileMenuOpen && (
        <button
          className="menu-overlay"
          onClick={closeMobileMenu}
          type="button"
          aria-label="Close menu overlay"
        ></button>
      )}
    </header>
  );
}
